import { animate, state, style, transition, trigger } from '@angular/animations'
import { CommonModule, DatePipe, NgClass, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common'
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { NgIcon, provideIcons } from '@ng-icons/core'
import {
  heroArchiveBox,
  heroBookmark,
  heroEye,
  heroFolder,
  heroMagnifyingGlass,
  heroPencil,
} from '@ng-icons/heroicons/outline'
import { heroBookmarkSolid } from '@ng-icons/heroicons/solid'
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons'
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs'
import { DialogService, KENDO_DIALOGS, WindowCloseResult, WindowRef, WindowService } from '@progress/kendo-angular-dialog'
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns'
import {
  CellTemplateDirective,
  ColumnComponent,
  FilterCellTemplateDirective,
  GridDataResult,
  KENDO_GRID,
  PageChangeEvent,
} from '@progress/kendo-angular-grid'
import { KENDO_INDICATORS } from '@progress/kendo-angular-indicators'
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs'
import { LabelComponent } from '@progress/kendo-angular-label'
import { CompositeFilterDescriptor, SortDescriptor, process } from '@progress/kendo-data-query'
import * as CryptoJS from 'crypto-js'
import { ToastrService } from 'ngx-toastr'
import { Subscription } from 'rxjs'
import { environment } from '../../../environments/environment'
import { DropdownFilterComponent } from '../../@theme/components/dropdown-filter/dropdown-filter.component'
import { InitUserService } from '../../@theme/services/init-user.service'
import { TransactionsServices } from '../../@theme/services/transactions.service'
import { ClientinfoComponent } from '../ClientInfo/clientinfo.component'
import { FilemanagerComponent } from '../FileManager/filemanager.component'
import { PdfSaveComponent } from '../pdfSaveas/pdfDirective'

// Rows requested from the paged PageResults_Transactions SP (@maximumRows).
// The grid pages client-side over this set.
const PENDING_ALERTS_MAX_ROWS = 500

@Component({
  selector: 'gd-pendingalerts',
  templateUrl: './pendingalerts.component.html',
  styleUrls: ['./pendingalerts.component.scss'],
  animations: [
    trigger('simpleFadeAnimation', [
      state('in', style({ opacity: 1 })),
      transition(':enter', [style({ opacity: 0 }), animate(600)]),
      transition(':leave', animate(600, style({ opacity: 0 }))),
    ]),
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DatePipe,
    NgClass,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    ColumnComponent,
    CellTemplateDirective,
    FilterCellTemplateDirective,
    DropdownFilterComponent,
    PdfSaveComponent,
    LabelComponent,
    NgIcon,
    KENDO_GRID,
    KENDO_DROPDOWNS,
    KENDO_DATEINPUTS,
    KENDO_INPUTS,
    KENDO_INDICATORS,
    KENDO_DIALOGS,
    KENDO_BUTTONS,
  ],
  providers: [
    provideIcons({
      heroEye,
      heroMagnifyingGlass,
      heroArchiveBox,
      heroPencil,
      heroFolder,
      heroBookmark,
      heroBookmarkSolid,
    }),
  ],
})
export class PendingalertsComponent implements OnInit, OnDestroy {
  private overlayEl!: HTMLDivElement
  private subscription = new Subscription()
  private roleing: string
  // Rows returned by the server; the grid view is processed from this.
  private listItems: any[] = []

  public pendingSpinner = true
  public hidebyRole = false
  public view: GridDataResult = { data: [], total: 0 }
  public pageSize = 20
  public skip = 0
  public sort: SortDescriptor[] = [{ field: 'dateReceived', dir: 'desc' }]
  // Grid filters live only for this visit; nothing is persisted.
  public filter: CompositeFilterDescriptor = { logic: 'and', filters: [] }

  // Search panel lookups (server-side filters).
  public branches: string[] = []
  public reps: any[] = []
  public statuses: any[] = []
  public defaultBranchItem = 'Filter By Branch'
  public defaultRepItem = { fullName: 'Filter By Rep', repId: null }
  public defaultStatusItem = { statusTypeDesc: 'Filter By Status', statusTypeId: null }
  public searchForm: UntypedFormGroup

  // Rep Name column filter source, built from the loaded rows (as on Pending).
  public passingrepfilter: string[] = []

  // Dialog / window state
  public RepNamInfo: any = []
  public blotterId: number
  public openAccountReadOnly = false
  public windowRef: WindowRef = null
  public viewallblot = ''

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private toastrService: ToastrService,
    private transactionService: TransactionsServices,
    private initUserService: InitUserService,
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private windowService: WindowService,
  ) {
    this.searchForm = this.formBuilder.group({
      branchNumber: null,
      repId: null,
      statusTypeId: null,
      receivedStartDate: null,
      receivedEndDate: null,
    })
    // Read-only: default the grid sort to the user's Pending sort preference.
    this.subscription.add(
      this.initUserService.userPreferences$.subscribe((prefs) => {
        if (prefs?.pendingSortField && prefs?.pendingSortDir) {
          this.sort = [{ field: prefs.pendingSortField, dir: prefs.pendingSortDir }]
          this.updateView()
        }
      }),
    )
  }

  ngOnInit() {
    const stored = localStorage.getItem('rvarietylist') ?? ''
    this.roleing = decodeURI(
      CryptoJS.AES.decrypt(stored, environment.securepw).toString(CryptoJS.enc.Utf8),
    )
    this.hidebyRole = this.roleing === 'HOME OFFICE USER'

    this.subscription.add(
      this.transactionService.getTransactionBranchNumbers({}).subscribe((r) => (this.branches = r ?? [])),
    )
    this.subscription.add(
      this.transactionService.getTransactionReps({}).subscribe((r) => (this.reps = r ?? [])),
    )
    this.subscription.add(
      this.transactionService.getTransactionStatuses({}).subscribe((r) => (this.statuses = r ?? [])),
    )
    this.getTransactionsWithPendingAlerts()
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  public isBookmarked(dataItem: any): boolean {
    return dataItem.bookm !== null && dataItem.bookm !== undefined
  }

  /*
  ***********
  Server search: PageResults_Transactions with AlertRep = 1
  ***********
  */
  getTransactionsWithPendingAlerts() {
    this.pendingSpinner = true
    this.subscription.add(
      this.transactionService.getTransactionPendingAlertsResults(this.buildSearchParams()).subscribe({
        next: (result) => {
          this.pendingSpinner = false
          this.listItems = result ?? []
          this.skip = 0
          this.updateDropdowns()
          this.updateView()
        },
        error: () => {
          this.pendingSpinner = false
          this.toastrService.error('Could not load transactions with pending alerts.', 'Error')
        },
      }),
    )
  }

  private buildSearchParams() {
    const f = this.searchForm.value
    // PascalCase to match the C# TransactionResultsRequest model.
    return {
      BranchNumber: f.branchNumber && f.branchNumber !== this.defaultBranchItem ? f.branchNumber : '',
      RepId: f.repId || 0,
      StatusTypeId: f.statusTypeId ?? null,
      ReceivedStartDate: f.receivedStartDate ?? null,
      ReceivedEndDate: f.receivedEndDate ?? null,
      HasPendingAlert: true,
      // The grid pages/sorts client-side over the rows fetched here.
      StartRowIndex: 0,
      MaximumRows: PENDING_ALERTS_MAX_ROWS,
      // Left empty so the server applies the user's Pending sort preference.
      Sort: null,
    }
  }

  onSearch() {
    this.getTransactionsWithPendingAlerts()
  }

  clearSearch() {
    this.searchForm.reset()
    this.getTransactionsWithPendingAlerts()
  }

  /*
  ***********
  Grid filtering / sorting / paging (client-side, same as Pending)
  ***********
  */
  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort.filter((s) => s.dir !== undefined)
    this.updateView()
  }

  public onFilterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter
    this.skip = 0
    this.updateView()
  }

  public onPageChange(event: PageChangeEvent): void {
    this.skip = event.skip
    this.pageSize = event.take
    this.updateView()
  }

  clearFilters(): void {
    this.filter = { logic: 'and', filters: [] }
    this.skip = 0
    this.updateView()
  }

  private updateView(): void {
    if (this.skip >= this.listItems.length) {
      this.skip = 0
    }
    this.view = process(this.listItems, {
      filter: this.filter,
      sort: this.sort,
      take: this.pageSize,
      skip: this.skip,
    })
  }

  private updateDropdowns(): void {
    this.passingrepfilter = Array.from(new Set(this.listItems.map((r) => r.repName))).filter(Boolean)
  }

  /*
  ***********
  Row actions
  ***********
  */
  viewClientInfo(clientId: number) {
    const ref = this.dialogService.open({
      title: 'View Client',
      content: ClientinfoComponent,
      maxHeight: '95%',
      width: '850px',
      maxWidth: '95%',
      actions: [{ text: 'Close', themeColor: 'primary' }],
    })
    ref.content.instance.clientId = clientId
  }

  updateBookmark(dataItem: any): void {
    const previous = dataItem.bookm
    if (this.isBookmarked(dataItem)) {
      // Flip the icon immediately; restore it if the call fails.
      dataItem.bookm = null
      this.transactionService.PostBookMarksRemove({ bookmarkId: previous }).subscribe({
        next: () => this.toastrService.success('Bookmark was removed', 'Success'),
        error: () => {
          dataItem.bookm = previous
          this.toastrService.error('Could not remove the bookmark.', 'Error')
        },
      })
    } else {
      // Placeholder until the real bookmark id arrives (needed to remove it).
      dataItem.bookm = -1
      this.transactionService.PostBookMarks({ blotterId: dataItem.blotterId }).subscribe({
        next: () => {
          this.toastrService.success('Bookmark was added', 'Success')
          this.refreshBookmarkIds()
        },
        error: () => {
          dataItem.bookm = null
          this.toastrService.error('Could not add the bookmark.', 'Error')
        },
      })
    }
  }

  // Re-read the rows quietly and copy just the bookmark ids across, so the
  // grid keeps its page, filters and scroll position.
  private refreshBookmarkIds(): void {
    this.subscription.add(
      this.transactionService.getTransactionPendingAlertsResults(this.buildSearchParams()).subscribe((rows) => {
        const ids = new Map<number, number | null>((rows ?? []).map((r: any) => [r.blotterId, r.bookm ?? null]))
        this.listItems.forEach((item) => {
          if (ids.has(item.blotterId)) {
            item.bookm = ids.get(item.blotterId)
          }
        })
        this.cdRef.markForCheck()
      }),
    )
  }

  openForm(data: any) {
    this.RepNamInfo = data
    this.blotterId = Number(data.blotterId)
    this.openAccountReadOnly = true
  }

  onDialogReciptClose() {
    this.openAccountReadOnly = false
    this.cdRef.detectChanges()
  }

  viewFiles(repId: string, clientName: string, blotterId: string, clientId: string, outquerystr: string): void {
    if (this.windowRef) {
      this.windowRef.close()
    }
    this.overlayEl = document.createElement('div')
    this.overlayEl.classList.add('k-overlay')
    document.body.appendChild(this.overlayEl)
    this.windowRef = this.windowService.open({
      title: 'File Manager',
      content: FilemanagerComponent,
      width: 1000,
      height: 675,
    })
    this.windowRef.result.subscribe((result) => {
      this.destroyOverlay()
      if (result instanceof WindowCloseResult) {
        this.windowRef = null
      }
    })
    const fileManager = this.windowRef.content.instance
    fileManager.outputClientId = clientId
    fileManager.outputrepId = repId
    fileManager.clientName = clientName
    fileManager.outquery = outquerystr
    fileManager.viewallblot = this.viewallblot
    fileManager.blotterId = blotterId
    fileManager.getFolderFiles()
  }

  private destroyOverlay(): void {
    if (this.overlayEl && document.body.contains(this.overlayEl)) {
      document.body.removeChild(this.overlayEl)
    }
  }

  /*
  ***********
  Navigation to the tab views (same query params as the Pending screen)
  ***********
  */
  editTransactions(
    blotID: number,
    clientId: number,
    repId: number,
    clt: string,
    statustype: number,
    tranactionstype: number,
    producttype: number,
    reviewtransaction: boolean,
    accountformreview: boolean,
    stateid: string,
    repCodeId: number,
  ) {
    if (!repCodeId) {
      this.toastrService.error('No Rep Code Id found for this transaction', 'Error!')
    }
    const common = {
      Rview: this.encryptProperty(blotID.toString()),
      clientId: this.encryptProperty(clientId.toString()),
      rId: this.encryptProperty(repId.toString()),
      cl: this.encryptProperty(clt),
      EditTrans: true,
      statustype: this.encryptProperty(statustype.toString()),
      transaction: this.encryptProperty(tranactionstype.toString()),
      product: this.encryptProperty(producttype.toString()),
      reviewtrans: reviewtransaction,
      accountformrvew: accountformreview,
      edittransactionShow: true,
      stateId: this.encryptProperty(stateid),
      repCodeId: this.encryptProperty(repCodeId?.toString()),
    }
    const queryParams =
      tranactionstype === 5
        ? { ...common, Cld: clientId, repunencrypt: repId }
        : { ...common, productTypeId: common.product, rIdunencrypt: repId }
    this.router.navigate(['/pages/TabsScreen/tab-views'], { queryParams })
  }

  reviewAccountTransactionsView(
    blotID: number,
    clientId: number,
    repId: number,
    clt: string,
    statustype: number,
    tranactionstype: number,
    producttype: number,
    reviewtransaction: boolean,
    accountformreview: boolean,
    statetype: string,
    repcodeId: number,
  ) {
    if (!repcodeId) {
      this.toastrService.error('No Rep Code Id found for this transaction', 'Error!')
    }
    const queryParams: any = {
      Rview: this.encryptProperty(blotID.toString()),
      clientId: this.encryptProperty(clientId.toString()),
      rId: this.encryptProperty(repId.toString()),
      cl: this.encryptProperty(clt),
      ReviewShow: true,
      statustype: this.encryptProperty(statustype.toString()),
      transaction: this.encryptProperty(tranactionstype.toString()),
      product: this.encryptProperty(producttype.toString()),
      paramAccReview: 'Accountreview',
      reviewtrans: reviewtransaction,
      accountformrvew: accountformreview,
      accountformShow: true,
      state: this.encryptProperty(statetype),
      repCodeId: this.encryptProperty(repcodeId?.toString()),
    }
    if (tranactionstype === 5) {
      queryParams.repunencrypt = repId
      queryParams.Cld = clientId
    }
    this.router.navigate(['/pages/TabsScreen/tab-views'], { queryParams })
  }

  ReviewAccountReviewTransactionsview(
    blotID: number,
    clientId: number,
    repId: number,
    clt: string,
    statustype: number,
    tranactionstype: number,
    producttype: number,
    reviewtransaction: boolean,
    accountformreview: boolean,
    statetype: string,
    repCodeId: number,
  ) {
    this.router.navigate(['/pages/TabsScreen/tab-views'], {
      queryParams: {
        Rview: this.encryptProperty(blotID.toString()),
        clientId: this.encryptProperty(clientId.toString()),
        rId: this.encryptProperty(repId.toString()),
        cl: this.encryptProperty(clt),
        ReviewShow: true,
        statustype: this.encryptProperty(statustype.toString()),
        transaction: this.encryptProperty(tranactionstype.toString()),
        product: this.encryptProperty(producttype.toString()),
        paramAccReview: 'Review',
        reviewtrans: reviewtransaction,
        accountformrvew: accountformreview,
        accountformShow: true,
        state: this.encryptProperty(statetype),
        repCodeId: this.encryptProperty(repCodeId?.toString()),
      },
    })
  }

  private encryptProperty(property: string): string {
    return CryptoJS.AES.encrypt(encodeURI(property ?? '').trim(), environment.securepw.trim()).toString()
  }
}
